// Shared upload endpoint contracts.
//
// Owns backend upload paths only. Direct S3 PUTs and FormData construction stay
// app-side because browser, React Native, and server runtimes build file bodies
// differently.

import { readUploadGrantRequest, readUploadVideoRequest } from '../upload-requests.js';
export { UploadRequestContractError, readUploadGrantRequest, readUploadVideoRequest, readUploadMaterialRequest } from '../upload-requests.js';

export const UPLOAD_PATHS = {
  s3Url: '/api/upload/s3-url',
  s3VideoUrl: '/api/upload/s3-video-url',
  uploadImage: '/api/upload/upload-image',
  uploadVideo: '/api/upload/upload-video',
  profileImage: '/api/upload/profile-image',
  materials: '/api/upload/materials',
};

export class UploadResponseContractError extends Error {
  constructor(operation, detail) {
    super(`Invalid upload ${operation} response: ${detail}`);
    this.name = 'UploadResponseContractError';
    this.code = 'INVALID_API_RESPONSE';
  }
}

const isRecord = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const isNonEmptyString = value => typeof value === 'string' && value.trim().length > 0;
const requireFields = (payload, operation, fields) => {
  if (!isRecord(payload) || !fields.every(field => isNonEmptyString(payload[field]))) {
    throw new UploadResponseContractError(operation, `expected non-empty ${fields.join(', ')} fields.`);
  }
  return payload;
};
const requireUrls = (payload, operation, fields) => {
  requireFields(payload, operation, fields);
  for (const field of fields) {
    let valid = false;
    try {
      const url = new URL(payload[field]);
      valid = payload[field] === payload[field].trim() && url.protocol === 'https:' && !url.username && !url.password;
    } catch { /* Invalid URL. */ }
    if (!valid) throw new UploadResponseContractError(operation, `expected an HTTPS ${field} URL without credentials.`);
  }
  return payload;
};
const readGrant = (payload, operation, field, prefix) => {
  requireFields(payload, operation, [field]);
  requireUrls(payload, operation, ['url']);
  const name = payload[field].slice(prefix.length);
  if (!payload[field].startsWith(prefix) || !/^[a-zA-Z0-9_.-]+$/.test(name) || name === '.' || name === '..') {
    throw new UploadResponseContractError(operation, `expected a managed ${field} path.`);
  }
  return payload;
};

export const readUploadS3UrlResponse = payload => readGrant(payload, 'S3 URL', 'key', 'profile-images/');
export const readUploadS3VideoUrlResponse = payload => readGrant(payload, 'video S3 URL', 'filePath', 'videos/');
export const readUploadImageResponse = payload => {
  requireFields(payload, 'image', ['message', 'originalUrl', 'thumbnailUrl']);
  requireUrls(payload, 'image', ['originalUrl', 'thumbnailUrl']);
  if (payload.message !== 'Upload successful') throw new UploadResponseContractError('image', 'expected the upload success message.');
  return payload;
};
export const readUploadVideoResponse = payload => {
  requireFields(payload, 'video', ['message', 'videoUrl', 'thumbnailUrl']);
  requireUrls(payload, 'video', ['videoUrl', 'thumbnailUrl']);
  if (payload.message !== 'Video thumbnail generated successfully') throw new UploadResponseContractError('video', 'expected the thumbnail success message.');
  return payload;
};
export const readUploadProfileImageResponse = payload => {
  requireFields(payload, 'profile image', ['message', 'imageUrl']);
  requireUrls(payload, 'profile image', ['imageUrl']);
  if (payload.message !== 'Upload successful') throw new UploadResponseContractError('profile image', 'expected the upload success message.');
  return payload;
};
export const readUploadMaterialsResponse = payload => {
  requireFields(payload, 'materials', ['message', 'url']);
  requireUrls(payload, 'materials', ['url']);
  if (payload.message !== 'Material uploaded successfully') throw new UploadResponseContractError('materials', 'expected the material success message.');
  return payload;
};

const filterParams = (params = {}) =>
  Object.fromEntries(
    Object.entries(params || {}).filter(([, value]) => value != null && value !== ''),
  );

export const buildUploadS3UrlPath = ({ fileName, fileType } = {}) => {
  const query = new URLSearchParams(filterParams({ fileName, fileType })).toString();
  return query ? `${UPLOAD_PATHS.s3Url}?${query}` : UPLOAD_PATHS.s3Url;
};

export const buildUploadS3VideoUrlPath = ({ fileName, fileType } = {}) => {
  const query = new URLSearchParams(filterParams({ fileName, fileType })).toString();
  return query ? `${UPLOAD_PATHS.s3VideoUrl}?${query}` : UPLOAD_PATHS.s3VideoUrl;
};

export const createUploadsClient = (adapter) => {
  if (!adapter || typeof adapter !== 'object') {
    throw new TypeError('createUploadsClient requires a transport adapter.');
  }

  return {
    async getS3Url(params = {}, options = {}) {
      readUploadGrantRequest(params);
      return adapter.get(UPLOAD_PATHS.s3Url, { ...options, params: filterParams(params) }).then(readUploadS3UrlResponse);
    },
    async getS3VideoUrl(params = {}, options = {}) {
      readUploadGrantRequest(params, true);
      return adapter.get(UPLOAD_PATHS.s3VideoUrl, { ...options, params: filterParams(params) }).then(readUploadS3VideoUrlResponse);
    },
    uploadImage(formData, options = {}) {
      return adapter.post(UPLOAD_PATHS.uploadImage, formData, options).then(readUploadImageResponse);
    },
    async uploadVideo(payload, options = {}) {
      readUploadVideoRequest(payload);
      return adapter.post(UPLOAD_PATHS.uploadVideo, payload, options).then(readUploadVideoResponse);
    },
    uploadProfileImage(formData, options = {}) {
      return adapter.post(UPLOAD_PATHS.profileImage, formData, options).then(readUploadProfileImageResponse);
    },
    uploadMaterials(formData, options = {}) {
      return adapter.post(UPLOAD_PATHS.materials, formData, options).then(readUploadMaterialsResponse);
    },
  };
};
